import { getAttachmentType, type AttachmentMeta } from "../models.ts";
import apiService from "./apiService.ts";
import type { PromiseCompletion } from "../classes/PromiseCompletion.ts";

class ImageService {
  /**
   * The server returns AttachmentMeta without the client-computed `type` field —
   * derive it locally from contentType/filename.
   */
  private _withType(meta: Omit<AttachmentMeta, 'type'>): AttachmentMeta {
    return { ...meta, type: getAttachmentType(meta.contentType, meta.filename) };
  }

  /**
   * Upload a file as a new attachment (refCount=1, unbound draft). Returns the metadata
   * of the created attachment; reference it by id when sending the message.
   * `onProgress` receives the upload fraction (0-1) as the bytes are sent.
   */
  public async uploadAttachment(file: File, loader?: PromiseCompletion, signal?: AbortSignal, onProgress?: (fraction: number) => void): Promise<AttachmentMeta> {
    const form = new FormData();
    form.append('file', file, file.name);
    const meta = await apiService.postForm<Omit<AttachmentMeta, 'type'>>('/image/upload', form, { loader, signal, onUploadProgress: onProgress });
    return this._withType(meta);
  }

  /**
   * Fetch attachment metadata by id (no refCount change).
   */
  public async getAttachmentMeta(attachmentId: string, loader?: PromiseCompletion): Promise<AttachmentMeta> {
    const meta = await apiService.get<Omit<AttachmentMeta, 'type'>>(`/image/${attachmentId}/meta`, { loader });
    return this._withType(meta);
  }

  /**
   * Increment the reference counter of an existing attachment (e.g. "use as reference"
   * or gallery re-add) and return its metadata.
   */
  public async addAttachmentRef(attachmentId: string, loader?: PromiseCompletion, signal?: AbortSignal): Promise<AttachmentMeta> {
    const meta = await apiService.post<Omit<AttachmentMeta, 'type'>>(`/image/${attachmentId}/ref`, undefined, { loader, signal });
    return this._withType(meta);
  }

  /**
   * Decrement the reference counters of the given attachments (called when an attachment
   * is removed from the composer before ever being bound to a message). Deletion happens
   * server-side when the counter reaches 0.
   */
  public async unrefAttachments(attachmentIds: string[]): Promise<void> {
    if (!attachmentIds.length) return;
    await apiService.post('/image/unref', { attachmentIds });
  }

  public validateImageFile(file: File): boolean {
    if (file.size > 7 * 1024 * 1024) { // 7MB limit
      alert('Image size exceeds 7MB limit.');
      return false;
    }

    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      alert('Only PNG, JPEG, and WEBP images are supported.');
      return false;
    }

    return true;
  }

  public async getFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
    const files: File[] = [];

    let imageProcessed = false;
    try {
      // Method 1: Check clipboard items (works on most desktop browsers)
      const items = Array.from(dataTransfer.items);
      const imageItem = items.find(item => item.type.startsWith('image/'));

      if (imageItem) {
        const file = imageItem.getAsFile();
        if (file && imageService.validateImageFile(file)) {
          files.push(file);
          imageProcessed = true;
        }
      }

      // Method 2: Check clipboard files (Android fallback)
      if (!imageProcessed && dataTransfer.files && dataTransfer.files.length > 0) {
        const files = Array.from(dataTransfer.files);
        const imageFile = files.find(file => file.type.startsWith('image/'));
        
        if (imageFile && imageService.validateImageFile(imageFile)) {
          files.push(imageFile);
          imageProcessed = true;
        }
      }

      // Method 3: Modern Clipboard API fallback (for newer browsers)
      if (!imageProcessed && navigator.clipboard && navigator.clipboard.read) {
        try {
          const clipboardItems = await navigator.clipboard.read();
          for (const clipboardItem of clipboardItems) {
            for (const type of clipboardItem.types) {
              if (type.startsWith('image/')) {
                const blob = await clipboardItem.getType(type);
                // Convert blob to file
                const file = new File([blob], 'pasted-image.' + type.split('/')[1], { type });
                if (imageService.validateImageFile(file)) {
                  files.push(file);
                  imageProcessed = true;
                  break;
                }
              }
            }
            if (imageProcessed) break;
          }
        } catch (clipboardError) {
          // Clipboard API not supported or permission denied, continue with other methods
          console.log('Clipboard API not available:', clipboardError);
        }
      }

    } catch (error) {
      console.error('Error processing pasted image:', error);
      alert('Failed to process the pasted image.');
    }

    return files;
  }

  // Supported attachment MIME types
  private static SUPPORTED_ATTACHMENT_TYPES = [
    'image/png', 'image/jpeg', 'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    // Audio types
    'audio/mpeg', 'audio/mp3', 'audio/webm', 'audio/ogg', 'audio/wav',
    'audio/x-wav', 'audio/mp4', 'audio/x-m4a', 'audio/flac', 'audio/aac'
  ];

  private static SUPPORTED_EXTENSIONS = [
    '.png', '.jpg', '.jpeg', '.webp',
    '.pdf',
    '.doc', '.docx',
    '.txt', '.md', '.csv',
    // Programming languages
    '.js', '.ts', '.jsx', '.tsx',
    '.py', '.java', '.cpp', '.c', '.h', '.hpp',
    '.cs', '.go', '.rb', '.php', '.swift', '.kt',
    '.rs', '.scala', '.r', '.m', '.mm',
    // Web files
    '.html', '.css', '.scss', '.sass', '.less',
    '.xml', '.svg',
    // Config/data files
    '.json', '.yaml', '.yml', '.toml', '.ini', '.cfg',
    '.log', '.sh', '.bat', '.ps1',
    // SQL and databases
    '.sql',
    // Other text files
    '.tex', '.rst', '.diff', '.patch',
    // Audio files
    '.mp3', '.ogg', '.wav', '.m4a', '.flac', '.aac'
  ];

  private static MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024; // 20MB for documents
  private static MAX_AUDIO_SIZE = 10 * 1024 * 1024; // 10MB for audio files

  public isAudioFile(file: File): boolean {
    return file.type.startsWith('audio/') ||
      ['.mp3', '.ogg', '.wav', '.m4a', '.flac', '.aac', '.webm'].some(
        ext => file.name.toLowerCase().endsWith(ext) && !file.type.startsWith('video/')
      );
  }

  public isFileSupported(file: File): boolean {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    return file.type.startsWith('audio/') ||
      ImageService.SUPPORTED_ATTACHMENT_TYPES.includes(file.type) ||
      ImageService.SUPPORTED_EXTENSIONS.includes(ext);
  }

  public isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  public validateAttachmentFile(file: File): boolean {
    if (this.isAudioFile(file)) {
      if (file.size > ImageService.MAX_AUDIO_SIZE) {
        alert(`Audio file size exceeds 10MB limit.`);
        return false;
      }
    } else if (file.size > ImageService.MAX_ATTACHMENT_SIZE) {
      alert(`File size exceeds ${ImageService.MAX_ATTACHMENT_SIZE / 1024 / 1024}MB limit.`);
      return false;
    }

    if (!this.isFileSupported(file)) {
      alert('Unsupported file type. Supported: images, PDF, DOC, DOCX, audio (MP3, WebM, OGG, WAV, M4A), and most text-based files.');
      return false;
    }

    return true;
  }

  /**
   * Get files from data transfer, supporting both images and documents.
   * For large pasted text, creates a text-fragment File.
   */
  public async getAttachmentsFromDataTransfer(dataTransfer: DataTransfer, forcePlain: boolean): Promise<{ files: File[], textFragment: string | null }> {
    const files: File[] = [];
    let textFragment: string | null = null;

    try {
      // Check for files first (images + documents)
      const items = Array.from(dataTransfer.items);

      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file && this.isFileSupported(file) && this.validateAttachmentFile(file)) {
            files.push(file);
          }
        }
      }

      // Fallback to dataTransfer.files
      if (files.length === 0 && dataTransfer.files && dataTransfer.files.length > 0) {
        for (const file of Array.from(dataTransfer.files)) {
          if (this.isFileSupported(file) && this.validateAttachmentFile(file)) {
            files.push(file);
          }
        }
      }

      // Check if large text is being pasted (text fragment)
      if (files.length === 0) {
        const textItem = items.find(item => item.kind === 'string' && item.type === 'text/plain');
        if (textItem) {
          const text = await new Promise<string>((resolve) => {
            textItem.getAsString(resolve);
          });
          // If pasted text is larger than 2000 chars or has more than 25 lines, treat as text fragment attachment.
          // When forcePlain is set (Shift held), always insert as plain text instead of an attachment.
          const linesCount = text.split('\n').length;
          if (!forcePlain && (text.length > 2000 || linesCount > 25)) {
            textFragment = text;
          }
        }
      }
    } catch (error) {
      console.error('Error processing pasted data:', error);
    }

    return { files, textFragment };
  }

  public createTextFragmentFile(text: string): File {
    const blob = new Blob([text], { type: 'text/plain' });
    const timestamp = Date.now();
    return new File([blob], `text-fragment-${timestamp}.txt`, { type: 'text/plain' });
  }
}
export const imageService = new ImageService();